/**
 * Shared utilities for country handling across PayeeWorkflows components
 * Consolidates duplicate country formatting functions
 */
import { GetCountryCodesQuery } from "#graphql/supergraphTypes";

export const US_NAME = "United States";

export type CountryData = GetCountryCodesQuery["countries"];

/**
 * Creates combined "Country Name:CountryID" format from country object
 * Used by CountrySelectionDropDown components
 */
export const getCountryValue = ({ id, name }: { code: string; name: string; id: string }) =>
  `${name}:${id}`;

/**
 * Extracts country ID from combined "Country Name:CountryID" format
 */
export const getIdFromCountry = (country?: string) => {
  if (!country) return "";
  const colonIndex = country.indexOf(":");
  if (colonIndex === -1) return "";
  return country.substring(colonIndex + 1).trim();
};

/**
 * Extracts country name from combined "Country Name:CountryID" format
 */
export const getNameFromCountry = (country?: string | null) => {
  if (!country) return "";
  const colonIndex = country.indexOf(":");
  if (colonIndex === -1) return "";
  return country.substring(0, colonIndex).trim();
};

/**
 * Converts country ID back to combined "Country Name:CountryID" format
 * Requires country data from GraphQL query
 */
export const getCountryValueFromId = (
  countryId?: string,
  countryData?: CountryData,
): string => {
  if (!countryId || !countryData?.edges) return "";

  const country = countryData.edges.find((edge) => edge?.node?.id === countryId);

  if (!country?.node?.name) return "";

  return `${country.node.name}:${countryId}`;
};

/**
 * Gets country name from country ID
 * Requires country data from GraphQL query
 */
export const getCountryNameFromId = (countryId?: string, countryData?: CountryData): string => {
  if (!countryId || !countryData?.edges) return "";

  const country = countryData.edges.find((edge) => edge?.node?.id === countryId);

  return country?.node?.name || "";
};
